// Herbstfest-Rückblick: Festwetter- und Dank-Mail zugestellt, Code HERBSTFEST10 genutzt (automatisch über die Frühlingsseite + Häkchen von Hand)
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { History, Loader2, RefreshCw, Check, X, Minus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/festwetter/rueckblick`;
const err = (e, f) => e.response?.data?.detail || f;
const day = (iso) => (iso ? new Date(iso).toLocaleDateString("de-DE", { timeZone: "Europe/Berlin" }) : "");
const btn = "flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/5 transition-colors disabled:opacity-50";
const SHOW = 60;

function Kpi({ label, value, sub, testid }) {
  return (
    <div className="bg-black/25 rounded-lg p-3" data-testid={testid}>
      <div className="text-lg font-semibold">{value}</div>
      <div className="text-[11px] text-eb-muted uppercase tracking-wider mt-0.5">{label}</div>
      {sub && <div className="text-[11px] text-eb-muted mt-1">{sub}</div>}
    </div>
  );
}

const mailKpi = (m) => (m.status === "versendet" ? `${m.sent} von ${m.total}` : m.status === "wird gesendet" ? "läuft …" : "noch nicht");
const Sent = ({ v }) => (v === true ? <Check size={14} className="text-emerald-300" /> : v === false ? <X size={14} className="text-rose-300" /> : <Minus size={14} className="text-eb-muted" />);

function Row({ r, onMark, busy }) {
  return (
    <div className="py-2 grid grid-cols-[1fr_44px_44px_150px] gap-2 items-center text-xs" data-testid={`rb-row-${r.email}`}>
      <span className="min-w-0 truncate"><b>{r.name || r.email}</b>{r.name && <span className="text-eb-muted"> · {r.email}</span>}</span>
      <span className="grid place-items-center"><Sent v={r.wetter} /></span>
      <span className="grid place-items-center"><Sent v={r.danke} /></span>
      {r.auto ? <span className="text-emerald-300 font-semibold" data-testid={`rb-auto-${r.email}`}>Anfrage {day(r.auto.at)}</span> : (
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!r.manual} disabled={busy} onChange={(e) => onMark(r.email, e.target.checked)} className="accent-emerald-500 w-4 h-4" data-testid={`rb-manual-${r.email}`} />
          <span className={r.manual ? "text-emerald-300" : "text-eb-muted"}>{r.manual ? `von Hand ${day(r.manual.at)}` : "Code genutzt"}</span>
        </label>
      )}
    </div>
  );
}

export function HerbstfestRueckblick() {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => axios.get(API).then((r) => setD(r.data)).catch((e) => setMsg(err(e, "Rückblick nicht ladbar"))), []);
  useEffect(() => { load(); }, [load]);
  const mark = async (email, used) => {
    setBusy(true); setMsg("");
    try { setD((await axios.put(`${API}/code`, { email, used })).data); } catch (e) { setMsg(err(e, "Nicht gespeichert")); }
    setBusy(false);
  };
  if (!d) return msg ? <div className="card text-sm text-rose-400">{msg}</div> : null;
  const rows = d.rows.filter((r) => !q || `${r.name} ${r.email}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="card space-y-4" data-testid="rueckblick-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="overline flex items-center gap-1"><History size={12} /> Herbstfest-Rückblick · Fest-Mails &amp; Code {d.voucher.code}</div>
        <button className={btn} onClick={load} data-testid="rb-reload"><RefreshCw size={13} /></button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Festwetter-Mail bekommen" value={mailKpi(d.mails.wetter)} sub={d.mails.wetter.status === "versendet" ? null : d.mails.wetter.send_label} testid="rb-kpi-wetter" />
        <Kpi label="Dank-Mail bekommen" value={mailKpi(d.mails.danke)} sub={d.mails.danke.status === "versendet" ? null : d.mails.danke.send_label} testid="rb-kpi-danke" />
        <Kpi label={`Code genutzt · ${d.guests} Gäste`} value={d.code.used} sub={`${d.code.auto} über die Frühlingsseite · ${d.code.manual} von Hand`} testid="rb-kpi-code" />
        <Kpi label="Frühlingsseite aufgerufen" value={d.page_views} sub="Seitenaufrufe, cookielos gezählt" testid="rb-kpi-views" />
      </div>
      {d.code.extern.length > 0 && <p className="text-xs text-amber-300" data-testid="rb-extern">Code auch von Nicht-Listen-Gästen genutzt: {d.code.extern.map((x) => x.name || x.email).join(", ")}</p>}
      {d.guests === 0 ? <p className="text-xs text-eb-muted" data-testid="rb-empty">Noch keine Gästeliste – sie wird im Festwetter-Hinweis eingefügt.</p> : (
        <div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Gast suchen …" data-testid="rb-search"
            className="bg-black/30 border border-white/10 rounded-md px-3 py-1.5 text-xs w-full sm:w-64" />
          <div className="grid grid-cols-[1fr_44px_44px_150px] gap-2 text-[10px] text-eb-muted uppercase tracking-wider mt-3"><span>Gast</span><span className="text-center">Wetter</span><span className="text-center">Dank</span><span>Code</span></div>
          <div className="divide-y divide-white/5" data-testid="rb-rows">{rows.slice(0, SHOW).map((r) => <Row key={r.email} r={r} onMark={mark} busy={busy} />)}</div>
          {rows.length > SHOW && <p className="text-[11px] text-eb-muted mt-2">… und {rows.length - SHOW} weitere – Suche nutzen</p>}
        </div>
      )}
      <p className="text-[11px] text-eb-muted">Automatisch gezählt: Anfragen über die Frühlingsseite (und jede .com-Anfrage mit dem Code). Buchungen per Telefon, E-Mail oder easybooking bitte von Hand abhaken.</p>
      {msg && <p className="text-xs text-rose-400" data-testid="rb-msg">{msg}</p>}
    </div>
  );
}
